import { FC, useEffect, useMemo, useState } from 'react'
import RadioInput from '../../../components/UI/Inputs/Radio/Simple'
import CustomButton from '../../../components/UI/Buttons/CustomButton'
import MainHeader from '../../../components/Calendar/Header'
import { useDispatch, useSelector } from '../../../store/index'
import { push } from 'connected-react-router'
import { isFulfilled } from '@reduxjs/toolkit'
import {
  deleteConnectedSocialAccountThunk,
  getAllProjectSettings,
  saveProjectSettings
} from '../../../store/project/thunks'
import {
  TProjectSocialFacebook,
  TProjectSocialInstagram,
  TProjectSocialLinkedin,
  TDeleteConnectedSocialAccountPayload,
  TProjectSocialTwitter,
  SETTING_NAME,
  TSaveProjectSettingsPayload
} from '../../../store/project/types'
import { removeProjectSocialAction } from '../../../store/project/slice'

import Facebook from '../../../assets/img/socials/facebook.svg'
import Instagram from '../../../assets/img/socials/instagram.svg'
import Linkedin from '../../../assets/img/socials/linkedin.svg'
import Twitter from '../../../assets/img/socials/twitter.svg'
import TrashIcon from '../../../components/UI/Icons/Trash'

import './SocialAccounts.scss'
import { SOCIAL_NETWORK } from '../../../models/post'
import { generatePath } from 'react-router'
import { AUTH_ROUTE } from '../../App/routes'
import { useToasts } from 'react-toast-notifications'
import { TOAST_MESSAGE } from '../../../components/UI/ToastNotification/ToastNotification'
import ConfirmPopup from '../../../components/UI/ConfirmPopup'
import { OriginalSocialIcons } from '../../../components/UI/Icons/socials'
import { setHeaderView } from '../../../store/application/slice'
import { HEADER_VIEW } from '../../../store/application/types'
import LoadableImage from '../../../components/UI/LoadableImage'

const socialLogos: { [key in SOCIAL_NETWORK]: string } = {
  [SOCIAL_NETWORK.FACEBOOK_SOCIAL]: Facebook,
  [SOCIAL_NETWORK.INSTAGRAM_SOCIAL]: Instagram,
  [SOCIAL_NETWORK.LINKEDIN_SOCIAL]: Linkedin,
  [SOCIAL_NETWORK.TWITTER_SOCIAL]: Twitter
}

type TProjectSocialDisplayName = {
  displayName: string
}

type TSocialNetworksList = {
  [SOCIAL_NETWORK.FACEBOOK_SOCIAL]: TProjectSocialFacebook & TProjectSocialDisplayName
  [SOCIAL_NETWORK.INSTAGRAM_SOCIAL]: TProjectSocialInstagram & TProjectSocialDisplayName
  [SOCIAL_NETWORK.LINKEDIN_SOCIAL]: TProjectSocialLinkedin & TProjectSocialDisplayName
  [SOCIAL_NETWORK.TWITTER_SOCIAL]: TProjectSocialTwitter & TProjectSocialDisplayName
}

type TEmptySocial = {
  social: SOCIAL_NETWORK
  icon: string
  displayName: string
}

type TMasterSocial = {
  name: SOCIAL_NETWORK | string
  id: number
  value: string | null
}

const emptySocials: TEmptySocial[] = [
  {
    social: SOCIAL_NETWORK.FACEBOOK_SOCIAL,
    icon: socialLogos.facebook,
    displayName: 'Facebook'
  },
  {
    social: SOCIAL_NETWORK.INSTAGRAM_SOCIAL,
    icon: socialLogos.instagram,
    displayName: 'Instagram'
  },
  {
    social: SOCIAL_NETWORK.LINKEDIN_SOCIAL,
    icon: Linkedin,
    displayName: 'Linkedin'
  },
  {
    social: SOCIAL_NETWORK.TWITTER_SOCIAL,
    displayName: 'Twitter',
    icon: Twitter
  }
]

interface SocialAccountsProps { }

const SocialAccounts: FC<SocialAccountsProps> = (props) => {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const { projectId, socialNetworks } = useSelector((state) => ({
    projectId: state.project.currentProject.id,
    socialNetworks: state.project.currentProject.socials
  }))

  const [postMasterContentSocialNetwork, setPostMasterContentSocialNetwork] =
    useState<null | SOCIAL_NETWORK>(null)
  const [inProgress, setInProgress] = useState<boolean>(false)
  const [socialNetworkForDeleting, setSocialNetworkForDeleting] = useState<SOCIAL_NETWORK | null>(
    null
  )

  useEffect(() => {
    const getProjectSettings = async () => {
      const {
        payload: { data }
      } = await dispatch(getAllProjectSettings(projectId))
      const { value }: { value: SOCIAL_NETWORK | null } = data.find(
        ({ name }: { name: SETTING_NAME }) =>
          name === SETTING_NAME.POST_MASTER_CONTENT_SOCIAL_NETWORK_ID
      )
      setPostMasterContentSocialNetwork(value)
    }

    getProjectSettings()
  }, [projectId])

  useEffect(() => {
    dispatch(setHeaderView(HEADER_VIEW.SOCIAL_ACCOUNTS))
  }, [])

  const masterSocialsContent: TMasterSocial[] = useMemo(() => {
    return [
      ...socialNetworks.map<TMasterSocial>(({ social_network, id }) => ({
        name: `${social_network[0].toLocaleUpperCase()}${social_network.slice(1)}`,
        id,
        value: social_network
      })),
      {
        name: 'Must approve all platforms',
        id: 0,
        value: null
      }
    ]
  }, [socialNetworks])

  const socialNetworksList: TSocialNetworksList = useMemo(() => {
    if (socialNetworks) {
      return socialNetworks.reduce(
        (
          acc: any,
          currentValue:
            | TProjectSocialInstagram
            | TProjectSocialFacebook
            | TProjectSocialLinkedin
            | TProjectSocialTwitter
        ) => {
          const { social_network: socialNetwork } = currentValue
          const localCopy: any = {
            [socialNetwork]: currentValue
          }
          switch (socialNetwork) {
            case SOCIAL_NETWORK.FACEBOOK_SOCIAL:
              acc[socialNetwork] = {
                ...currentValue,
                displayName: localCopy[socialNetwork].account_details.fb_page_name
              }
              return acc
            case SOCIAL_NETWORK.INSTAGRAM_SOCIAL:
              acc[socialNetwork] = {
                ...currentValue,
                displayName: localCopy[socialNetwork].account_details.fb_page_name
              }
              return acc
            case SOCIAL_NETWORK.LINKEDIN_SOCIAL:
              acc[socialNetwork] = {
                ...currentValue,
                displayName: localCopy[socialNetwork].account_details.organizationName
              }
              return acc
            case SOCIAL_NETWORK.TWITTER_SOCIAL:
              acc[socialNetwork] = {
                ...currentValue,
                displayName: localCopy[socialNetwork].account_details.screen_name
              }
              return acc
          }
        },
        {}
      )
    }
    return {}
  }, [socialNetworks])

  const loginSocialNetwork = (social: SOCIAL_NETWORK) => {
    switch (social) {
      case SOCIAL_NETWORK.FACEBOOK_SOCIAL:
        dispatch(push(`/${projectId}/facebook/connect`))
        break
      case SOCIAL_NETWORK.INSTAGRAM_SOCIAL:
        dispatch(push(`/${projectId}/instagram/connect`))
        break
      case SOCIAL_NETWORK.LINKEDIN_SOCIAL:
        dispatch(push(`/${projectId}/linkedin/connect`))
        break
      case SOCIAL_NETWORK.TWITTER_SOCIAL:
        dispatch(push(`/${projectId}/twitter/connect`))
        break
    }
  }

  const handleChangeMasterContent = ({
    value,
    name
  }: {
    value: SOCIAL_NETWORK | null
    name: any
  }) => {
    setPostMasterContentSocialNetwork(value ? value : null)
  }

  const setMasterContentSetting = async () => {
    setInProgress(true)
    const payload: TSaveProjectSettingsPayload = {
      project_id: projectId,
      settings: [
        {
          name: SETTING_NAME.POST_MASTER_CONTENT_SOCIAL_NETWORK_ID,
          value: postMasterContentSocialNetwork
        }
      ]
    }
    const response = await dispatch(saveProjectSettings(payload))
    if (isFulfilled(response)) {
      addToast(TOAST_MESSAGE.CHANGE_SAVED, { appearance: 'success' })
      setInProgress(false)
    } else {
      addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
      setInProgress(false)
    }
  }

  const onConfirmClose = async () => {
    setSocialNetworkForDeleting(null)
  }

  const onConfirmDelete = async () => {
    if (projectId) {
      let payload: TDeleteConnectedSocialAccountPayload = {
        project_id: projectId,
        social: socialNetworkForDeleting!
      }

      await dispatch(deleteConnectedSocialAccountThunk(payload))
      onConfirmClose()
      dispatch(removeProjectSocialAction(socialNetworkForDeleting!))
    }
  }

  return (
    <div className='social-accounts-page-wrapper'>
      <MainHeader />
      <div className='social-accounts-page-container'>
        <div className='social-accounts-list'>
          <div className='social-accounts-page-container_title'>Social Accounts</div>
          {emptySocials.map(({ social, displayName, icon }) => (
            <div key={social} className='social-accounts-page-container_social-network'>
              <div key={social} className='social-network_icon'>
                {OriginalSocialIcons[social]}
              </div>
              {socialNetworksList[social] ? (
                <div className='social-network_user-wrapper'>
                  <div className='social-network_user-information'>
                    <LoadableImage
                      media={socialNetworksList[social].logo
                        ? socialNetworksList[social].logo
                        : socialLogos[social]}
                    />
                    <div>
                      <div className='social-network_user-information-name'>
                        {socialNetworksList[social].displayName}
                      </div>
                      <div className='social-network_user-information-role'>{/* Writer */}</div>
                    </div>
                  </div>
                  <div
                    className='social-network_user-remove'
                    onClick={() => setSocialNetworkForDeleting(social)}
                  >
                    <TrashIcon />
                  </div>
                </div>
              ) : (
                <div
                  className='social-network_add-account'
                  onClick={() => loginSocialNetwork(social)}
                >
                  Add {displayName} Account
                </div>
              )}
              <div className='social-network_arrow'></div>
            </div>
          ))}
        </div>
        <div className='social-accounts-list'>
          <div className='social-accounts-page-container_title'>Set Master Approval Platform</div>
          {masterSocialsContent.map(({ name, id, value }) => (
            <div key={id} className='social-network_add-account master-content-social'>
              <RadioInput
                key={`${value}_master`}
                checked={postMasterContentSocialNetwork === value}
                handleChange={handleChangeMasterContent}
                label={name}
                value={value}
                name={id}
              />
            </div>
          ))}
          <CustomButton
            className='main-page-header__actions__create-content'
            onClick={setMasterContentSetting}
            button='primary'
            size='small'
            inProgress={inProgress}
          >
            Apply
          </CustomButton>
        </div>
      </div>
      <ConfirmPopup
        onClose={onConfirmClose}
        onConfirm={onConfirmDelete}
        isModalOpen={!!socialNetworkForDeleting && !!projectId}
      >
        {socialNetworkForDeleting &&
          `Are you sure you want to delete ${socialNetworksList[socialNetworkForDeleting].displayName} account?`}
      </ConfirmPopup>
    </div>
  )
}

export default SocialAccounts
